import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Brain, ChevronRight, FileText, FlaskConical, Loader2, Package, Search, X } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState';
import { labApi } from '../lib/api';

interface LabProduct {
  id: string;
  name: string;
  niche: string | null;
  status: string;
  imageUrl?: string | null;
  referenceImages?: Array<{ id: string; updatedAt: string }>;
  knowledgeBases: Array<{ id: string; isComplete: boolean }>;
  contents: Array<{ id: string; type: string; status: string }>;
}

interface LabBrand {
  id: string;
  name: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  DRAFT: { label: 'Szkic', color: 'text-text-secondary', bg: 'bg-surface-hover' },
  INPUT: { label: 'Dane', color: 'text-text-secondary', bg: 'bg-surface-hover' },
  ANALYZING: { label: 'Analizowanie', color: 'text-accent-info', bg: 'bg-accent-info/15' },
  KNOWLEDGE_READY: { label: 'Baza gotowa', color: 'text-success', bg: 'bg-success/15' },
  GENERATING: { label: 'Generowanie', color: 'text-warning', bg: 'bg-warning/15' },
  COMPLETE: { label: 'Gotowy', color: 'text-success', bg: 'bg-success/15' },
};

function contentTypeCount(contents: LabProduct['contents']): number {
  return new Set(contents.map(content => content.type)).size;
}

function contentTypeLabel(count: number): string {
  if (count === 1) return '1 typ';
  if (count >= 2 && count <= 4) return `${count} typy`;
  return `${count} typów`;
}

export function LabProductListPage() {
  const { brandId } = useParams();
  const unbranded = !brandId;
  const [brands, setBrands] = useState<LabBrand[]>([]);
  const [products, setProducts] = useState<LabProduct[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectedBrand = useMemo(
    () => brands.find(brand => brand.id === brandId) || null,
    [brandId, brands],
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [productResponse, brandResponse] = await Promise.all([
        labApi.getLabProducts({
          limit: 200,
          search: search || undefined,
          brandId: brandId || undefined,
          unbranded: unbranded || undefined,
        }),
        labApi.getBrands(),
      ]);
      setProducts(productResponse.data.data || []);
      setBrands(brandResponse.data.data?.brands || []);
    } catch (loadError) {
      console.error('Nie udało się pobrać produktów Laboratorium:', loadError);
      setError('Nie udało się pobrać produktów tej marki.');
    } finally {
      setLoading(false);
    }
  }, [brandId, search, unbranded]);

  useEffect(() => {
    const timeout = window.setTimeout(() => { void load(); }, 250);
    return () => window.clearTimeout(timeout);
  }, [load]);

  const title = unbranded ? 'Bez marki' : selectedBrand?.name || 'Laboratorium marki';
  const databaseLink = unbranded
    ? '/marketing/baza-produktow?unbranded=true'
    : `/marketing/baza-produktow?brandId=${encodeURIComponent(brandId || '')}`;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/marketing/baza-produktow?view=laboratory" className="mb-3 inline-flex items-center gap-1.5 text-xs text-text-muted transition-colors hover:text-text-primary">
          <ArrowLeft className="h-3.5 w-3.5" />
          Wszystkie marki
        </Link>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <span className="rounded-xl bg-accent-primary/10 p-2.5 text-accent-primary"><FlaskConical className="h-6 w-6" /></span>
              <h1 className="text-3xl font-bold text-text-primary">{title}</h1>
            </div>
            <p className="mt-2 text-sm text-text-secondary">Tylko produkty, dla których utworzono już Laboratorium.</p>
          </div>
          <Link to={databaseLink} className="btn-secondary inline-flex items-center gap-2 self-start lg:self-auto">
            Otwórz produkty marki w Bazie
          </Link>
        </div>
      </div>

      <div className="relative max-w-xl">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
        <input
          type="text"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Szukaj w Laboratorium marki..."
          className="w-full rounded-lg border border-border bg-surface-raised py-2.5 pl-10 pr-10 text-sm text-text-primary transition-colors hover:border-border-strong focus:border-accent-primary/50 focus:outline-none"
        />
        {search && (
          <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary" aria-label="Wyczyść wyszukiwanie">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {error && <p role="alert" className="rounded-lg border border-danger/25 bg-danger/10 p-3 text-sm text-danger">{error}</p>}

      {loading ? (
        <div className="flex min-h-56 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-accent-primary" /></div>
      ) : products.length === 0 ? (
        <EmptyState
          icon={FlaskConical}
          title={search ? 'Brak wyników' : 'Brak produktów w Laboratorium'}
          description={search ? 'Zmień wyszukiwaną frazę.' : 'Przypisz produkt do tej marki w Bazie produktów i utwórz dla niego Laboratorium.'}
          buttonLabel="Przejdź do Bazy produktów"
          buttonTo={databaseLink}
        />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {products.map(product => {
            const status = STATUS_CONFIG[product.status] || STATUS_CONFIG.DRAFT;
            const completeKnowledge = product.knowledgeBases.filter(base => base.isComplete).length;
            const totalKnowledge = product.knowledgeBases.length;
            const completeContent = product.contents.filter(content => content.status === 'COMPLETE').length;
            return (
              <Link
                key={product.id}
                to={`/marketing/laboratorium/produkt/${product.id}`}
                className="group overflow-hidden rounded-card border border-border bg-surface-raised transition-all hover:-translate-y-0.5 hover:border-accent-primary/40 hover:shadow-lg"
              >
                <div className="relative aspect-[16/9] overflow-hidden border-b border-border-subtle bg-gradient-to-br from-accent-primary/15 via-surface-hover to-accent-info/10">
                  {product.referenceImages?.[0] ? (
                    <img src={labApi.getReferenceImageFileUrl(product.id, product.referenceImages[0].id, product.referenceImages[0].updatedAt)} alt="" className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.03]" />
                  ) : (
                    <div className="flex h-full items-center justify-center"><Package className="h-12 w-12 text-accent-primary/30" /></div>
                  )}
                  <span className={`absolute right-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-semibold backdrop-blur-sm ${status.bg} ${status.color}`}>{status.label}</span>
                </div>
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <h2 className="truncate text-base font-semibold text-text-primary transition-colors group-hover:text-accent-primary">{product.name}</h2>
                      <span className="mt-1.5 inline-flex rounded-full bg-surface-hover px-2 py-0.5 text-[10px] text-text-muted">
                        {product.niche || 'Brak kategorii'}
                      </span>
                    </div>
                    <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-text-disabled transition-transform group-hover:translate-x-0.5 group-hover:text-accent-primary" />
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2 border-t border-border-subtle pt-3">
                    <div className="rounded-lg bg-surface-hover/50 p-2.5">
                      <span className="flex items-center gap-1 text-[10px] text-text-muted"><Brain className="h-3 w-3" />Bazy wiedzy</span>
                      <strong className="mt-1 block text-sm font-semibold text-text-primary">{completeKnowledge}/{totalKnowledge}</strong>
                    </div>
                    <div className="rounded-lg bg-surface-hover/50 p-2.5">
                      <span className="flex items-center gap-1 text-[10px] text-text-muted"><FileText className="h-3 w-3" />Treści</span>
                      <strong className="mt-1 block text-sm font-semibold text-text-primary">{completeContent} · {contentTypeLabel(contentTypeCount(product.contents))}</strong>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
